import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import AHDate from "_shared/dates/date";
import DateSchema from "_shared/dates/date-schema";
import Guid from "_shared/guids/guid";
import GuidSchema from "_shared/guids/guid-schema";
import ForumSchema from "messaging/forums/_schemas/forum-schema";
import Forum from "messaging/forums/forum";
import { ForumInvitationStatus } from "messaging/forums/subscribers/invitations/types";
import { z } from "zod";

export type ForumInvitationJSON = z.infer<typeof ForumInvitationSchema.ForumInvitationJSONZ>;
export type ForumInvitationParams = z.infer<typeof ForumInvitationSchema.ForumInvitationZ>;

export default class ForumInvitationSchema {
  public static readonly ForumInvitationJSONZ = z.object({
    id: z.uuid(),
    inviteeId: z.uuid(),
    forum: ForumSchema.ForumJSONZ,
    invitationMessage: z.string().nullish(),
    createdDate: z.iso.datetime({ offset: true }),
    status: z.enum(ForumInvitationStatus),
  });

  public static readonly ForumInvitationZ = z.object({
    id: z.instanceof(Guid),
    inviteeId: z.instanceof(Guid),
    forum: z.instanceof(Forum),
    invitationMessage: z.string().min(1).optional(),
    createdDate: DateSchema.type,
    status: z.enum(ForumInvitationStatus),
  });

  public static validate(values: unknown): ForumInvitationParams {
    return parseOrThrow(ForumInvitationSchema.ForumInvitationZ, values, {
      logError: true,
      schemaName: "ForumInvitationSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(json: unknown): ForumInvitationJSON {
    return parseOrThrow(ForumInvitationSchema.ForumInvitationJSONZ, json, {
      logError: true,
      schemaName: "ForumInvitationSchema",
      operation: "validateJSON",
      payloadSample: json,
    });
  }

  public static isValid(values: unknown): values is ForumInvitationParams {
    try {
      ForumInvitationSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(json: unknown): json is ForumInvitationJSON {
    try {
      ForumInvitationSchema.validateJSON(json);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(json: ForumInvitationJSON): ForumInvitationParams {
    if (isNullOrUndefined(json)) return json;
    const parsed = parseOrThrow(ForumInvitationSchema.ForumInvitationJSONZ, json, {
      logError: true,
      schemaName: "ForumInvitationSchema",
      operation: "deserialize",
      payloadSample: json,
    });
    return ForumInvitationSchema.codec.decode(parsed);
  }

  public static serialize(params: ForumInvitationParams): ForumInvitationJSON {
    if (isNullOrUndefined(params)) return params;
    const parsed = ForumInvitationSchema.validate(params);
    if (isNullOrUndefined(parsed)) return parsed;
    return ForumInvitationSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    ForumInvitationSchema.ForumInvitationJSONZ,
    ForumInvitationSchema.ForumInvitationZ,
    {
      decode: (data) => ({
        id: new Guid(GuidSchema.deserialize(data.id)),
        inviteeId: new Guid(GuidSchema.deserialize(data.inviteeId)),
        forum: Forum.fromJSON(data.forum),
        invitationMessage: z.string().min(1).parse(data.invitationMessage),
        createdDate: new AHDate(DateSchema.deserialize(data.createdDate)),
        status: data.status,
      }),
      encode: (invite) => ({
        id: invite.id.toJSON(),
        inviteeId: invite.inviteeId.toJSON(),
        forum: invite.forum.toJSON(),
        invitationMessage: invite.invitationMessage,
        createdDate: invite.createdDate.toJSON(),
        status: invite.status,
      }),
    }
  );
}
